import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Loader2, Send, CornerDownRight, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { FeedPostContent } from './FeedPostContent';

/**
 * FeedCommentsPanel — inline comments + replies + @mention autocomplete.
 *
 * Props:
 *  - postId: string
 *  - onCountChange?: (newCount) => void
 */
const FeedCommentsPanel = ({ postId, onCountChange }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [value, setValue] = useState('');
  const [replyTo, setReplyTo] = useState(null); // { id, author_name }
  const [suggestions, setSuggestions] = useState([]);
  const [mentionState, setMentionState] = useState(null); // { anchor, query, sel }
  const [resolvedMentions, setResolvedMentions] = useState({}); // user_id -> {name}
  const textareaRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/feed/${postId}/comments`);
      setComments(res.data.comments || []);
    } catch (e) {
      // 404/403 — silently empty
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const onChange = async (e) => {
    const v = e.target.value;
    setValue(v);
    const caret = e.target.selectionStart || v.length;
    // Find last unfinished @mention before caret
    const before = v.slice(0, caret);
    const match = before.match(/(^|\s)@([\w.-]{0,30})$/);
    if (match) {
      const query = match[2];
      setMentionState({ anchor: caret - query.length, query });
      try {
        const res = await api.get(`/feed/mention-suggestions?q=${encodeURIComponent(query)}`);
        setSuggestions(res.data.items || []);
      } catch {
        setSuggestions([]);
      }
    } else {
      setMentionState(null);
      setSuggestions([]);
    }
  };

  const selectMention = (item) => {
    // Replace the partial `@query` with `@Name ` and record the mapping
    const caret = textareaRef.current?.selectionStart || value.length;
    const before = value.slice(0, caret);
    const after = value.slice(caret);
    const newBefore = before.replace(/(^|\s)@([\w.-]{0,30})$/, (m, pre) => `${pre}@${item.name} `);
    const next = newBefore + after;
    setValue(next);
    setResolvedMentions((prev) => ({ ...prev, [item.user_id]: { name: item.name, role: item.role } }));
    setMentionState(null);
    setSuggestions([]);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const submit = async () => {
    const content = value.trim();
    if (!content) return;
    // Deduce which resolvedMentions are actually still present in the text
    const present = Object.entries(resolvedMentions)
      .filter(([, m]) => content.includes(`@${m.name}`))
      .map(([uid]) => uid);
    setSending(true);
    try {
      const res = await api.post(`/feed/${postId}/comment`, {
        content,
        parent_comment_id: replyTo?.id || null,
        mentions: present,
      });
      // Iter-156 P0 — bump parent count BEFORE the comments re-fetch so
      // the toggle badge updates immediately. Previously the count badge
      // appeared "stuck" because the prop callback fired after `await
      // load()` (a second round-trip) which masked the update.
      onCountChange && onCountChange(res.data.comment_id);
      setValue('');
      setReplyTo(null);
      setResolvedMentions({});
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to post comment');
    } finally {
      setSending(false);
    }
  };

  // Build tree
  const topLevel = comments.filter((c) => !c.parent_comment_id);
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parent_comment_id) {
      (acc[c.parent_comment_id] = acc[c.parent_comment_id] || []).push(c);
    }
    return acc;
  }, {});

  // Iter-251 — Comment likes
  const toggleCommentLike = async (commentId) => {
    // Optimistic update
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId
          ? {
              ...c,
              user_liked: !c.user_liked,
              likes_count: (c.likes_count || 0) + (c.user_liked ? -1 : 1),
            }
          : c,
      ),
    );
    try {
      const res = await api.post(`/community/feed-comments/${commentId}/like`);
      // Reconcile with server-authoritative count
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, user_liked: res.data.liked, likes_count: res.data.likes_count }
            : c,
        ),
      );
    } catch (e) {
      // Revert
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? {
                ...c,
                user_liked: !c.user_liked,
                likes_count: (c.likes_count || 0) + (c.user_liked ? -1 : 1),
              }
            : c,
        ),
      );
      toast.error('Could not update like');
    }
  };

  return (
    <div className="mt-3 border-t border-gray-100 pt-3" data-testid={`feed-comments-${postId}`}>
      {loading ? (
        <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
      ) : (
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          {topLevel.length === 0 && (
            <p className="text-xs text-gray-400 italic">Be the first to comment.</p>
          )}
          {topLevel.map((c) => (
            <div key={c.id} className="space-y-1.5">
              <CommentRow
                comment={c}
                onReply={() => setReplyTo({ id: c.id, author_name: c.author_name })}
                onLike={() => toggleCommentLike(c.id)}
              />
              {(repliesByParent[c.id] || []).map((r) => (
                <div key={r.id} className="ml-6 flex items-start gap-1.5">
                  <CornerDownRight className="w-3 h-3 text-gray-300 mt-1" />
                  <div className="flex-1">
                    <CommentRow
                      comment={r}
                      reply
                      onReply={() => setReplyTo({ id: c.id, author_name: r.author_name })}
                      onLike={() => toggleCommentLike(r.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 space-y-2 relative">
        {replyTo && (
          <div className="flex items-center justify-between bg-cyan-50 border border-cyan-200 rounded px-2 py-1 text-xs text-cyan-800">
            <span>Replying to <strong>{replyTo.author_name}</strong></span>
            <button onClick={() => setReplyTo(null)} className="text-cyan-600 hover:underline" data-testid="feed-cancel-reply-btn">Cancel</button>
          </div>
        )}
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          placeholder={replyTo ? 'Write a reply… use @ to mention' : 'Write a comment… use @ to mention'}
          rows={2}
          className="text-sm"
          data-testid={`feed-comment-input-${postId}`}
        />
        {mentionState && suggestions.length > 0 && (
          <div className="absolute left-0 right-0 -bottom-1 translate-y-full bg-white border border-gray-200 rounded-md shadow-md z-20 max-h-48 overflow-y-auto">
            {suggestions.map((s) => (
              <button
                key={s.user_id}
                onClick={() => selectMention(s)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-cyan-50 text-xs"
                data-testid={`feed-mention-item-${s.user_id}`}
              >
                {s.photo ? (
                  <img src={s.photo} alt="" className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-600">
                    {(s.name || '?').slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="flex-1">{s.name}</span>
                <span className="text-[10px] text-gray-400">{s.role}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={submit}
            disabled={sending || !value.trim()}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid={`feed-send-comment-btn-${postId}`}
          >
            {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
            {replyTo ? 'Reply' : 'Comment'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CommentRow = ({ comment, reply = false, onReply, onLike }) => (
  <div className={`flex items-start gap-2 ${reply ? '' : ''}`}>
    {comment.author_photo ? (
      <img src={comment.author_photo} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
    ) : (
      <span className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-600 flex-shrink-0">
        {(comment.author_name || '?').slice(0, 1).toUpperCase()}
      </span>
    )}
    <div className="flex-1 min-w-0">
      <div className="bg-gray-50 rounded-lg px-2.5 py-1.5">
        <div className="text-[11px] font-semibold text-gray-800">{comment.author_name}</div>
        <FeedPostContent content={comment.content} className="text-xs text-gray-700 whitespace-pre-wrap break-words" />
      </div>
      <div className="flex items-center gap-3 mt-0.5 ml-1">
        <span className="text-[10px] text-gray-400">
          {comment.created_at ? new Date(comment.created_at).toLocaleString() : ''}
        </span>
        <button
          onClick={onLike}
          className={`text-[10px] flex items-center gap-0.5 ${comment.user_liked ? 'text-rose-600' : 'text-gray-500 hover:text-rose-600'}`}
          data-testid={`feed-comment-like-btn-${comment.id}`}
        >
          <Heart className={`w-3 h-3 ${comment.user_liked ? 'fill-rose-500' : ''}`} />
          {comment.likes_count > 0 && <span>{comment.likes_count}</span>}
        </button>
        <button
          onClick={onReply}
          className="text-[10px] text-gray-500 hover:text-cyan-600"
          data-testid={`feed-reply-btn-${comment.id}`}
        >
          Reply
        </button>
      </div>
    </div>
  </div>
);

export default FeedCommentsPanel;
